import { useCallback, useEffect, useState } from 'react';
import {
  BadgeNew,
  Colors,
  Flex,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import type { FloatingButton } from '../../api/promotionsService/getPromotionComponents/types';
import {
  getRemainTime,
  getRemainTimeUnit,
  isNil,
  isNotNil,
  localStorageBridge,
} from '../../utils';
import { useForegroundInterval, useOpenDeeplink } from '../../hooks';

const BUTTON_SIZE = 48;

const FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY =
  `FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY_` as const;

type PromotionFloatingButtonProps = {
  button: FloatingButton;
  promotionSno?: number;
  /**
   * 클릭 시 호출되는 로깅 콜백. 컨텍스트별 SCREEN / 추가 analytics 등은
   * 호출 측에서 주입한다. (e.g. GOODS_DETAIL 의 GOODS_SNO, BUTTON_INDEX, etc.)
   */
  onClickLog: () => void;
};

/**
 * 프로모션 플로팅 버튼. 서버가 내려주는 floating_button 정보로 렌더하고
 * 클릭 시 deeplink 열기 + 신규 뱃지 dismiss 처리.
 *
 * iOS `PromotionComponentFloatingButton` 와 대응.
 */
export const PromotionFloatingButton = ({
  button,
  promotionSno,
  onClickLog,
}: PromotionFloatingButtonProps) => {
  const [isClickOnce, setIsClickOnce] = useState(false);
  const [isShowText, setIsShowText] = useState(true);
  const [remainTime, setRemainTime] = useState('');
  const { openDeeplink } = useOpenDeeplink();

  const onPressButton = useCallback(() => {
    onClickLog();
    setIsClickOnce(true);

    localStorageBridge.putBoolean(
      `${FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY}${promotionSno}`,
      true,
    );

    openDeeplink(button.item.deeplink);
  }, [button.item.deeplink, onClickLog, promotionSno, openDeeplink]);

  const checkClickOncePromotion = useCallback(async () => {
    if (isNil(promotionSno)) return;
    const value = isNotNil(
      await localStorageBridge.getBoolean(
        `${FLOATING_BUTTON_NEW_BADGE_SHOW_LOCAL_KEY}${promotionSno}`,
      ),
    );
    setIsClickOnce(value);
  }, [promotionSno]);

  useEffect(() => {
    checkClickOncePromotion();
  }, [checkClickOncePromotion]);

  const { timer } = button.item.render;

  const remainTimeUnit = isNotNil(timer)
    ? getRemainTimeUnit(timer.expired_at)
    : null;

  const remainTimeDelay = (() => {
    switch (remainTimeUnit) {
      case 'minute':
        return 1000 * 60;
      case 'second':
        return 1000;
      default:
        return null;
    }
  })();

  // 텍스트 토글 (3초)
  useForegroundInterval(
    () => setIsShowText(prev => !prev),
    isNotNil(timer) ? 3000 : null,
    { immediate: false },
  );

  // 남은 시간 업데이트
  useForegroundInterval(() => {
    if (isNotNil(timer)) {
      const time = getRemainTime(timer.expired_at);
      setRemainTime(`${time} 남음`);
    }
  }, remainTimeDelay);

  return (
    <Flex
      width={BUTTON_SIZE}
      height={BUTTON_SIZE}
      borderRadius={16}
      borderWidth={1}
      alignItems={'center'}
      justifyContent={'center'}
      shadowStep={'medium'}
      pointerEvents={'auto'}
      borderColorValue={
        button.item.render.border_color ?? Colors.border_secondary
      }
      backgroundColorValue={button.item.render.background_color}
      onPress={onPressButton}
    >
      <RemoteImage uri={button.item.render.icon} width={20} height={20} />
      {!isShowText && isNotNil(button.item.render.timer) ? (
        <Typography
          type={'subtitle7'}
          numberOfLines={1}
          rawColor={button.item.render.timer.text_color}
        >
          {remainTime}
        </Typography>
      ) : (
        <Typography
          type={'subtitle7'}
          numberOfLines={1}
          rawColor={button.item.render.text_color}
        >
          {button.item.render.text}
        </Typography>
      )}
      {!isClickOnce && (
        <Flex position={'absolute'} top={0} right={0}>
          <BadgeNew />
        </Flex>
      )}
    </Flex>
  );
};
